import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import {
  Dialog as DialogComponent,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface IProps {
  className?: string;
  title: string;
  description?: React.ReactNode;
  trigger: React.ReactNode;
  tooltipContent?: React.ReactNode;
  onClose?: () => void;
  children?:
    | React.ReactNode
    | React.FC<{
        open: boolean;
        handleOpen: () => void;
        handleClose: () => void;
      }>;
}

interface IDialogContext {
  open: boolean;
  handleOpen: () => void;
  handleClose: () => void;
}

const DialogContext = createContext<IDialogContext>({} as IDialogContext);

export const useDialog = () => useContext(DialogContext);

const Dialog: React.FC<IProps> = ({
  className,
  title,
  trigger,
  description,
  children,
  onClose,
  tooltipContent,
}) => {
  const [open, setOpen] = useState(false);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setOpen(open);
      if (!open && onClose) onClose();
    },
    [onClose],
  );

  const value = useMemo<IDialogContext>(
    () => ({
      open,
      handleOpen: () => setOpen(true),
      handleClose: () => setOpen(false),
    }),
    [open],
  );

  return (
    <DialogContext.Provider value={value}>
      <DialogComponent open={open} onOpenChange={handleOpenChange}>
        {tooltipContent ? (
          <Tooltip>
            <TooltipTrigger>
              <DialogTrigger asChild>{trigger}</DialogTrigger>
            </TooltipTrigger>
            <TooltipContent>{tooltipContent}</TooltipContent>
          </Tooltip>
        ) : (
          <DialogTrigger asChild>{trigger}</DialogTrigger>
        )}

        <DialogContent className={cn('sm:max-w-[525px]', className)}>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>
          {typeof children === 'function'
            ? children({
                open: value.open,
                handleOpen: value.handleOpen,
                handleClose: value.handleClose,
              })
            : children}
        </DialogContent>
      </DialogComponent>
    </DialogContext.Provider>
  );
};

export default Dialog;
